import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, Heart, Loader2, ArrowUpRight, Newspaper } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export function BlogCard({ blog, index = 0 }) {
  return (
    <Reveal delay={(index % 3) * 0.07}>
      <Link to={`/blogs/${blog.slug}`} data-testid={`blog-card-${blog.slug}`}
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_22px_55px_rgba(10,41,30,0.1)]">
        {blog.image_url ? (
          <div className="relative aspect-[16/9] overflow-hidden bg-forest/5">
            <img src={blog.image_url} alt={blog.title} loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
            <span className="absolute left-4 top-4 rounded-full bg-forest-deep/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
              {blog.category}
            </span>
          </div>
        ) : (
          <div className="flex aspect-[16/9] items-center justify-center bg-gradient-to-br from-forest-deep to-forest">
            <Newspaper className="h-10 w-10 text-gold/40" />
          </div>
        )}
        <div className="flex flex-1 flex-col p-6">
          {!blog.image_url && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">{blog.category}</span>
          )}
          <h3 className="mt-2 font-display text-lg font-bold leading-snug text-forest-deep transition-colors group-hover:text-forest">
            {blog.title}
          </h3>
          <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">
            {(blog.excerpt || "").slice(0, 120)}{blog.excerpt?.length > 120 ? "…" : ""}
          </p>
          <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
            <span>{blog.author} · {fmtDate(blog.published_at)}</span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-gold-dark" />{blog.read_time || 1} min</span>
              <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5 text-gold-dark" />{blog.likes || 0}</span>
              <ArrowUpRight className="h-4 w-4 text-forest/40 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold-dark" />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

export default function BlogsPage() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/blogs", { params: { search, category } })
        .then((r) => setData(r.data))
        .catch(() => setData({ blogs: [], categories: [] }));
    }, 300);
    return () => clearTimeout(t);
  }, [search, category]);

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="blogs-page">
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span><span>Resources</span><span>/</span>
              <span className="text-gold">Blogs</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl">
              Field notes from{" "}
              <em className="font-editorial font-medium italic text-gold">the consulting desk.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              Practical guides on admissions, careers, language tests, and Quranic studies —
              written by the same people who advise our clients.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center" data-testid="blog-filters">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="blog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles, topics, tags…"
                className="h-12 w-full rounded-full border border-input bg-white pl-11 pr-4 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="blog-filter-all" onClick={() => setCategory("")}
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  category === "" ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                }`}>
                All
              </button>
              {(data?.categories || []).map((c) => (
                <button key={c} data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCategory(c)}
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    category === c ? "border-forest bg-forest text-bone" : "border-border bg-white text-foreground/60 hover:border-forest/40"
                  }`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blogs-grid">
          {data === null ? (
            <div className="col-span-full flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
          ) : data.blogs.length === 0 ? (
            <div className="col-span-full rounded-3xl border border-dashed border-border bg-white py-20 text-center" data-testid="blogs-empty">
              <Newspaper className="mx-auto h-9 w-9 text-forest/20" />
              <p className="mt-4 font-display text-lg font-bold text-forest-deep">No articles found</p>
              <p className="mt-1.5 text-sm text-muted-foreground">Try a different search or category.</p>
            </div>
          ) : (
            data.blogs.map((b, i) => <BlogCard key={b.id} blog={b} index={i} />)
          )}
        </div>
      </div>
    </div>
  );
}
